import { useEffect, useState } from "react";
import { ExternalLink, Loader2, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TierOverrideDialog } from "./TierOverrideDialog";
import { CreditGrantsPanel } from "./CreditGrantsPanel";
import { getStripeEnvironment } from "@/lib/stripe";

type Props = { userId: string; onTierChanged?: () => void };

type Detail = {
  subscription: null | {
    stripe_customer_id: string;
    stripe_subscription_id: string;
    price_id: string;
    status: string;
    cancel_at_period_end: boolean;
    current_period_end: string | null;
    pending_tier: string | null;
    pending_effective_at: string | null;
  };
  stripe: null | { status?: string; cancel_at_period_end?: boolean; current_period_end?: number | null; trial_end?: number | null; error?: string };
  price_meta: null | { unit_amount: number | null; currency: string; interval: string | null; product_name: string | null };
  invoices: Array<{ id: string; amount_paid: number; amount_due: number; currency: string; status: string | null; created: number; hosted_invoice_url: string | null; number: string | null }>;
};

const fmtMoney = (cents: number, currency: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);

const statusVariant = (s?: string | null) => {
  if (!s) return "secondary" as const;
  if (s === "active" || s === "trialing") return "default" as const;
  if (s === "past_due" || s === "unpaid" || s === "incomplete") return "destructive" as const;
  return "secondary" as const;
};

export const BillingCard = ({ userId, onTierChanged }: Props) => {
  const { isSuperAdmin } = useUserRole();
  const env = getStripeEnvironment();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [busy, setBusy] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("admin-billing-user", {
      body: { user_id: userId, environment: env },
    });
    setLoading(false);
    if (error) {
      toast.error(`Could not load billing: ${error.message}`);
      return;
    }
    setDetail(data as Detail);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [userId]);

  const openPortal = async () => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("admin-billing-portal", {
      body: { user_id: userId, environment: env },
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    const url = (data as { url?: string } | null)?.url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const customerHref = (cust: string) => {
    const base = env === "live" ? "https://dashboard.stripe.com" : "https://dashboard.stripe.com/test";
    return `${base}/customers/${cust}`;
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">Billing ({env})</div>
        {isSuperAdmin && (
          <Button size="sm" variant="outline" onClick={() => setOverrideOpen(true)}>
            Apply tier override
          </Button>
        )}
      </div>

      {loading && <Skeleton className="h-24 w-full" />}

      {!loading && detail && !detail.subscription && (
        <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
          No {env} subscription on file. Tier overrides still apply manually.
        </p>
      )}

      {!loading && detail?.subscription && (
        <div className="space-y-3 rounded-md border border-border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusVariant(detail.stripe?.status ?? detail.subscription.status)}>
              {detail.stripe?.status ?? detail.subscription.status}
            </Badge>
            {detail.subscription.cancel_at_period_end && (
              <Badge variant="outline">cancels at period end</Badge>
            )}
            {detail.subscription.pending_tier && (
              <Badge variant="outline">
                pending → {detail.subscription.pending_tier}
                {detail.subscription.pending_effective_at && ` on ${new Date(detail.subscription.pending_effective_at).toLocaleDateString()}`}
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-2 gap-y-1 text-xs text-muted-foreground">
            <span>Plan</span>
            <span className="text-right text-foreground">
              {detail.price_meta?.product_name ?? "—"}
              {detail.price_meta?.unit_amount != null && (
                <> · {fmtMoney(detail.price_meta.unit_amount, detail.price_meta.currency)}/{detail.price_meta.interval ?? ""}</>
              )}
            </span>
            <span>Price ID</span>
            <span className="text-right font-mono text-foreground truncate">{detail.subscription.price_id}</span>
            <span>Period end</span>
            <span className="text-right text-foreground">
              {detail.subscription.current_period_end ? new Date(detail.subscription.current_period_end).toLocaleDateString() : "—"}
            </span>
            <span>Customer</span>
            <span className="text-right">
              <a href={customerHref(detail.subscription.stripe_customer_id)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 font-mono text-foreground hover:underline">
                {detail.subscription.stripe_customer_id.slice(0, 14)}… <ExternalLink className="h-3 w-3" />
              </a>
            </span>
          </div>

          {isSuperAdmin && (
            <Button size="sm" variant="outline" disabled={busy} onClick={openPortal}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReceiptText className="h-4 w-4" />}
              Open billing portal
            </Button>
          )}

          {detail.invoices.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground">Recent invoices</div>
              <ul className="space-y-1 text-xs">
                {detail.invoices.map((inv) => (
                  <li key={inv.id} className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">{new Date(inv.created * 1000).toLocaleDateString()}</span>
                    <span>{fmtMoney(inv.amount_paid || inv.amount_due, inv.currency)}</span>
                    <Badge variant={statusVariant(inv.status)} className="text-[10px]">{inv.status ?? "—"}</Badge>
                    {inv.hosted_invoice_url ? (
                      <a className="inline-flex items-center gap-1 hover:underline" href={inv.hosted_invoice_url} target="_blank" rel="noreferrer">
                        view <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : <span className="text-muted-foreground">—</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <CreditGrantsPanel userId={userId} />

      <TierOverrideDialog
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        userId={userId}
        onApplied={async () => { await load(); onTierChanged?.(); }}
      />
    </section>
  );
};
